import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, query, where, getDocs, orderBy, onSnapshot } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, storage, db } from '../firebase';
import 'react-phone-number-input/style.css';

const ApplicationForm = () => {
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [deckFile, setDeckFile] = useState(null);
  const [deckUploading, setDeckUploading] = useState(false);
  const [hasExistingApplication, setHasExistingApplication] = useState(false);
  const [checkingApplication, setCheckingApplication] = useState(true);
  const [activeCohort, setActiveCohort] = useState(null);
  const [loadingCohort, setLoadingCohort] = useState(true);
  const [selectedSessions, setSelectedSessions] = useState([]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    setError,
    clearErrors
  } = useForm({
    defaultValues: {
      firstName: user?.displayName?.split(' ')[0] || '',
      lastName: user?.displayName?.split(' ')[1] || '',
      workEmail: ''
    }
  });

  // Watch fields for conditional logic
  const countryServed = watch('countryServed');
  const isFounder = watch('isFounder');
  const roleInCompany = watch('roleInCompany');
  const hasLinkedIn = watch('hasLinkedIn');
  const canCommit = watch('canCommit');
  const howHeardAbout = watch('howHeardAbout');
  const combatRole = watch('combatRole');

  // Check if user has already submitted an application
  useEffect(() => {
    const checkExistingApplication = async () => {
      if (!user?.uid) {
        setCheckingApplication(false);
        return;
      }

      try {
        // Ensure user is fully authenticated
        await user.getIdToken();
        
        // Small delay to ensure authentication is complete
        await new Promise(resolve => setTimeout(resolve, 100));
        const q = query(
          collection(db, 'applications'),
          where('userId', '==', user.uid)
        );
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          setHasExistingApplication(true);
        }
      } catch (error) {
        console.error('Error checking for existing application:', error);
      } finally {
        setCheckingApplication(false);
      }
    };

    checkExistingApplication();
  }, [user]);

  // Load active cohort and its sessions
  useEffect(() => {
    const loadActiveCohort = () => {
      // Get all cohorts and check their application windows (same logic as Dashboard)
      const q = query(collection(db, 'cohorts'), orderBy('createdAt', 'desc'));
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        console.log('Cohort query result:', snapshot.docs.length, 'cohorts found');
        const now = new Date();
        let activeWindow = null;
        
        // Check each cohort for active application windows
        for (const doc of snapshot.docs) {
          const cohort = doc.data();
          const phase1Start = cohort.timeline?.phase1Start ? new Date(cohort.timeline.phase1Start) : null;
          const phase1End = cohort.timeline?.phase1Deadline ? new Date(cohort.timeline.phase1Deadline) : null;
          
          console.log('Checking cohort for active window:', {
            name: cohort.name,
            status: cohort.status,
            phase1Start: phase1Start?.toISOString(),
            phase1End: phase1End?.toISOString(),
            isOpen: phase1Start && phase1End && now >= phase1Start && now <= phase1End,
            phase2Sessions: cohort.phase2Sessions?.length || 0
          });
          
          if (phase1Start && phase1End && now >= phase1Start && now <= phase1End) {
            // Found an active application window
            activeWindow = {
              id: doc.id,
              ...cohort
            };
            break; // Use the first active window found
          }
        }
        
        if (activeWindow) {
          console.log('Active cohort found:', activeWindow);
          console.log('Phase 2 sessions:', activeWindow.phase2Sessions);
          setActiveCohort(activeWindow);
        } else {
          console.log('No active cohort found with open application window');
          setActiveCohort(null);
        }
        setLoadingCohort(false);
      }, (error) => {
        console.error('Error loading active cohort:', error);
        setLoadingCohort(false);
      });

      return unsubscribe;
    };

    const unsubscribe = loadActiveCohort();
    return () => unsubscribe();
  }, []);

  // Military units data removed - now using open text inputs

  // Email domain validation - show warning but don't block
  const validateEmailDomain = (email) => {
    const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    return !personalDomains.includes(domain);
  };

  const isPersonalEmail = (email) => {
    const personalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com', 'icloud.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    return personalDomains.includes(domain);
  };

  // LinkedIn URL validation
  const validateLinkedInURL = (url) => {
    if (!url) return true; // Optional field
    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
    return linkedinRegex.test(url);
  };

  // File upload with Dropzone
  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'];
    const allowedExtensions = ['.pdf', '.ppt', '.pptx'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      setError('deckFile', { message: 'Please upload a PDF or PowerPoint file (PDF, PPT, PPTX)' });
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setError('deckFile', { message: 'File size must be less than 10MB' });
      return;
    }

    setDeckUploading(true);
    clearErrors('deckFile');

    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `pitch-decks/${user.uid}/${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      setDeckFile({ name: file.name, url: downloadURL });
      setValue('deckUrl', downloadURL);
      
    } catch (error) {
      console.error('Upload error:', error);
      setError('deckFile', { message: 'Failed to upload file. Please try again.' });
    } finally {
      setDeckUploading(false);
    }
  }, [user?.uid, setValue, setError, clearErrors]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx']
    },
    maxFiles: 1
  });

  // Frontend flag detection (moved from cloud functions for better performance)
  const detectFlags = (applicationData) => {
    const redFlags = [];
    const yellowFlags = [];
    
    // RED FLAGS (Auto-reject)
    
    // Must have served in USA or Israel
    const countryServed = applicationData.countryServed?.trim().toLowerCase();
    if (!["usa", "israel", "united states", "us"].includes(countryServed)) {
      redFlags.push("Did not serve in USA or Israel");
    }
    
    // YELLOW FLAGS (Manual review required)
    
    // LinkedIn profile missing
    if (!applicationData.linkedin || applicationData.linkedin.trim() === "") {
      yellowFlags.push("No LinkedIn profile provided");
    }
    
    // Company website missing
    if (!applicationData.companyWebsite || applicationData.companyWebsite.trim() === "") {
      yellowFlags.push("No company website provided");
    }
    
    // Work email missing
    if (!applicationData.workEmail || applicationData.workEmail.trim() === "") {
      yellowFlags.push("No work email provided");
    }
    
    // Non-professional work email domain
    if (applicationData.workEmail && isPersonalEmail(applicationData.workEmail)) {
      yellowFlags.push("Non-professional work email domain");
    }
    
    // Must be a founder
    if (applicationData.isFounder === false) {
      yellowFlags.push("Applicant role is not founder");
    }
    
    // No pitch deck uploaded and no explanation
    if ((!applicationData.deckUrl || applicationData.deckUrl.trim() === "") && 
        (!applicationData.noDeckReason || applicationData.noDeckReason.trim() === "")) {
      yellowFlags.push("No pitch deck uploaded and no explanation provided");
    }
    
    // Check combat role - explicit yes/no field
    if (applicationData.combatRole === "no") {
      yellowFlags.push("Did not serve in a combat role");
    }
    
    // Cannot commit to full program
    if (applicationData.canCommit === false) {
      yellowFlags.push("Cannot commit to full program");
    }
    
    const hasRedFlag = redFlags.length > 0;
    const hasYellowFlag = yellowFlags.length > 0;
    
    // Determine status
    let status;
    if (hasRedFlag) {
      status = "auto_rejected";
    } else if (hasYellowFlag) {
      status = "manual_review";
    } else {
      status = "auto_approved";
    }
    
    return {
      redFlags,
      yellowFlags,
      hasRedFlag,
      hasYellowFlag,
      status
    };
  };

  // Form submission
  const onSubmit = async (data) => {
    setLoading(true);
    setSubmitError('');

    try {
      // Additional validations
      if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
        setError('phone', { message: 'Please enter a valid phone number with country code' });
        setLoading(false);
        return;
      }

      if (hasLinkedIn === 'yes' && data.linkedinUrl && !validateLinkedInURL(data.linkedinUrl)) {
        setError('linkedinUrl', { message: 'Please enter a valid LinkedIn profile URL' });
        setLoading(false);
        return;
      }

      // Validate cohort and session selection
      if (!activeCohort) {
        setSubmitError('No active cohort available. Please contact support.');
        setLoading(false);
        return;
      }

      if (!selectedSessions || selectedSessions.length < 1 || selectedSessions.length > 2) {
        setSubmitError('Please select at least 1 and at most 2 preferred session times.');
        setLoading(false);
        return;
      }


      // Prepare submission data
      console.log('User email:', user.email); // Debug log
      const applicationData = {
        // Basic Info
        companyName: data.companyName,
        companyWebsite: data.companyWebsite || null,
        firstName: data.firstName,
        lastName: data.lastName,
        isFounder: data.isFounder === 'yes',
        role: data.roleInCompany,
        workEmail: data.workEmail || null,
        email: user.email, // User's authentication email as fallback
        phone: phoneNumber,
        linkedin: hasLinkedIn === 'yes' ? data.linkedinUrl : null,
        
        // Service History
        countryServed: data.countryServed === 'other' ? data.otherCountryServed : data.countryServed,
        unitServed: data.unitServed,
        roleInService: data.roleInService,
        combatRole: data.combatRole,
        serviceStartYear: data.serviceStartYear,
        serviceEndYear: data.serviceEndYear,
        
        // Company Info
        grandmaTest: data.grandmaTest,
        
        // Pitch Deck
        deckUrl: data.deckUrl || null,
        noDeckReason: data.noDeckReason || null,
        
        // Discovery & Commitment
        howHeardAbout: data.howHeardAbout === 'other' ? data.otherHowHeardAbout : data.howHeardAbout,
        canCommit: canCommit === 'yes',
        
        // Cohort and session selection
        cohortId: activeCohort?.id,
        selectedSessions: selectedSessions,
        
        // System Fields
        userId: user.uid,
        status: 'pending',
        emailSent: false,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      console.log('Application data being submitted:', applicationData); // Debug log

      // Run flag detection on the frontend (moved from cloud functions for better performance)
      const flagResults = detectFlags(applicationData);
      
      // Add flag results to application data
      const applicationDataWithFlags = {
        ...applicationData,
        redFlags: flagResults.redFlags,
        yellowFlags: flagResults.yellowFlags,
        hasRedFlag: flagResults.hasRedFlag,
        hasYellowFlag: flagResults.hasYellowFlag,
        status: flagResults.status, // Override the 'pending' status with detected status
        processedAt: new Date(),
        currentPhase: 1,
        phase1CompletedAt: new Date()
      };

      console.log('Flag detection results:', flagResults);

      // Submit to Firestore with flags already processed
      await addDoc(collection(db, 'applications'), applicationDataWithFlags);
      
      // Show success immediately - background processing happens separately
      setSubmitSuccess(true);
      
    } catch (error) {
      console.error('Submission error:', error);
      
      // Provide more specific error messages
      if (error.code === 'permission-denied') {
        setSubmitError('Permission denied. Please ensure you are using the same email address as your account.');
      } else if (error.code === 'unavailable') {
        setSubmitError('Service temporarily unavailable. Please try again in a few moments.');
      } else {
        setSubmitError('Failed to submit application. Please check your internet connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking for existing application
  if (checkingApplication) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking application status...</p>
        </div>
      </div>
    );
  }

  // Show message if user has already submitted an application
  if (hasExistingApplication) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Application Already Submitted</h2>
          <p className="text-gray-600 mb-6">
            You have already submitted an application to the VB Accelerator Program. Only one application per user is allowed.
          </p>
          <p className="text-gray-600 mb-6">
            You can check your application status on your dashboard.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Application Submitted!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for applying to the VB Accelerator Program. We'll review your application and get back to you soon.
          </p>
          <button
            onClick={() => window.location.href = '/'}
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg">
          <div className="px-8 py-6 border-b border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900">VB Accelerator Application</h1>
            <p className="mt-2 text-gray-600">Please fill out all required fields</p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="px-8 py-6 space-y-8">
            {submitError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                {submitError}
              </div>
            )}

            {/* Company Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Name *
                  </label>
                  <input
                    {...register('companyName', { required: 'Company name is required' })}
                    onChange={(e) => {
                      register('companyName').onChange(e);
                      if (e.target.value.trim()) clearErrors('companyName');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.companyName && <p className="text-red-600 text-sm mt-1">{errors.companyName.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Company Website (if you have one)
                  </label>
                  <input
                    type="url"
                    {...register('companyWebsite')}
                    placeholder="https://example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </section>

            {/* Personal Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Personal Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    {...register('firstName', { required: 'First name is required' })}
                    onChange={(e) => {
                      register('firstName').onChange(e);
                      if (e.target.value.trim()) clearErrors('firstName');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.firstName && <p className="text-red-600 text-sm mt-1">{errors.firstName.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    {...register('lastName', { required: 'Last name is required' })}
                    onChange={(e) => {
                      register('lastName').onChange(e);
                      if (e.target.value.trim()) clearErrors('lastName');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.lastName && <p className="text-red-600 text-sm mt-1">{errors.lastName.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Are you a founder? *
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        {...register('isFounder', { required: 'Please select an option' })}
                        value="yes"
                        onChange={(e) => {
                          register('isFounder').onChange(e);
                          if (e.target.value) clearErrors('isFounder');
                        }}
                        className="mr-2"
                      />
                      Yes
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        {...register('isFounder')}
                        value="no"
                        onChange={(e) => {
                          register('isFounder').onChange(e);
                          if (e.target.value) clearErrors('isFounder');
                        }}
                        className="mr-2"
                      />
                      No
                    </label>
                  </div>
                  {errors.isFounder && <p className="text-red-600 text-sm mt-1">{errors.isFounder.message}</p>}
                  {isFounder === 'no' && (
                    <p className="text-amber-600 text-sm mt-1">⚠️ Note: Non-founders will require manual review</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Role in the Company *
                  </label>
                  <input
                    type="text"
                    {...register('roleInCompany', { required: 'Role is required' })}
                    onChange={(e) => {
                      register('roleInCompany').onChange(e);
                      if (e.target.value.trim()) clearErrors('roleInCompany');
                    }}
                    placeholder="e.g., CEO, Founder, Co-Founder, CTO, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.roleInCompany && <p className="text-red-600 text-sm mt-1">{errors.roleInCompany.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Work Email (Optional)
                    <span className="text-sm text-gray-500 block">Professional email preferred for business communications</span>
                  </label>
                  <input
                    type="email"
                    {...register('workEmail')}
                    placeholder="your.email@company.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {watch('workEmail') && isPersonalEmail(watch('workEmail')) && (
                    <p className="text-amber-600 text-sm mt-1">
                      <i className="fas fa-circle text-yellow-600 mr-1"></i>
                      Professional email domains are recommended for business applications
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <PhoneInput
                    international
                    countryCallingCodeEditable={false}
                    defaultCountry="US"
                    value={phoneNumber}
                    onChange={(value) => {
                      setPhoneNumber(value);
                      // Clear phone error when user enters a valid number
                      if (value && isValidPhoneNumber(value)) {
                        clearErrors('phone');
                      }
                    }}
                    className="w-full"
                    style={{
                      '--PhoneInputCountryFlag-height': '1em',
                      '--PhoneInput-color--focus': '#3B82F6'
                    }}
                  />
                  {errors.phone && <p className="text-red-600 text-sm mt-1">{errors.phone.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Do you have a LinkedIn profile? *
                  </label>
                  <div className="flex space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        {...register('hasLinkedIn', { required: 'Please select an option' })}
                        value="yes"
                        onChange={(e) => {
                          register('hasLinkedIn').onChange(e);
                          if (e.target.value) clearErrors('hasLinkedIn');
                        }}
                        className="mr-2"
                      />
                      Yes
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        {...register('hasLinkedIn')}
                        value="no"
                        onChange={(e) => {
                          register('hasLinkedIn').onChange(e);
                          if (e.target.value) clearErrors('hasLinkedIn');
                        }}
                        className="mr-2"
                      />
                      No
                    </label>
                  </div>
                  {errors.hasLinkedIn && <p className="text-red-600 text-sm mt-1">{errors.hasLinkedIn.message}</p>}
                  {hasLinkedIn === 'no' && (
                    <p className="text-amber-600 text-sm mt-1">⚠️ Note: No LinkedIn profile will require manual review</p>
                  )}
                </div>

                {hasLinkedIn === 'yes' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      LinkedIn Profile URL *
                    </label>
                    <input
                      type="url"
                      {...register('linkedinUrl', { 
                        required: hasLinkedIn === 'yes' ? 'LinkedIn URL is required' : false,
                        validate: validateLinkedInURL
                      })}
                      onChange={(e) => {
                        register('linkedinUrl').onChange(e);
                        if (e.target.value.trim()) clearErrors('linkedinUrl');
                      }}
                      placeholder="https://linkedin.com/in/yourname"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {errors.linkedinUrl && <p className="text-red-600 text-sm mt-1">{errors.linkedinUrl.message}</p>}
                  </div>
                )}
              </div>
            </section>

            {/* Service History */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Service History</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    What country did you serve in? *
                  </label>
                  <select
                    {...register('countryServed', { required: 'Country served is required' })}
                    onChange={(e) => {
                      register('countryServed').onChange(e);
                      if (e.target.value) clearErrors('countryServed');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select country</option>
                    <option value="us">United States</option>
                    <option value="israel">Israel</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.countryServed && <p className="text-red-600 text-sm mt-1">{errors.countryServed.message}</p>}
                  {countryServed === 'other' && (
                    <>
                      <input
                        type="text"
                        placeholder="Please specify the country"
                        {...register('otherCountryServed', { 
                          required: countryServed === 'other' ? 'Please specify the country' : false 
                        })}
                        className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        onChange={(e) => {
                          register('otherCountryServed').onChange(e);
                          if (e.target.value) clearErrors('otherCountryServed');
                        }}
                      />
                      {errors.otherCountryServed && <p className="text-red-600 text-sm mt-1">{errors.otherCountryServed.message}</p>}
                    </>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    What unit(s) did you serve in? *
                  </label>
                  <input
                    type="text"
                    {...register('unitServed', { required: 'Unit served is required' })}
                    onChange={(e) => {
                      register('unitServed').onChange(e);
                      if (e.target.value.trim()) clearErrors('unitServed');
                    }}
                    placeholder="e.g., Navy SEALs, 82nd Airborne, Paratroopers Brigade"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.unitServed && <p className="text-red-600 text-sm mt-1">{errors.unitServed.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    What was your role? *
                  </label>
                  <input
                    type="text"
                    {...register('roleInService', { required: 'Service role is required' })}
                    onChange={(e) => {
                      register('roleInService').onChange(e);
                      if (e.target.value.trim()) clearErrors('roleInService');
                    }}
                    placeholder="e.g., Infantry Officer, Squad Leader, Sniper, Pilot"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {errors.roleInService && <p className="text-red-600 text-sm mt-1">{errors.roleInService.message}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Did you serve in a combat role? *
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        {...register('combatRole', { required: 'Please specify if you served in a combat role' })}
                        value="yes"
                        onChange={(e) => {
                          register('combatRole').onChange(e);
                          if (e.target.value) clearErrors('combatRole');
                        }}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="text-gray-900">Yes, I served in a combat role</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        {...register('combatRole', { required: 'Please specify if you served in a combat role' })}
                        value="no"
                        onChange={(e) => {
                          register('combatRole').onChange(e);
                          if (e.target.value) clearErrors('combatRole');
                        }}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      />
                      <span className="text-gray-900">No, I did not serve in a combat role</span>
                    </label>
                  </div>
                  {errors.combatRole && <p className="text-red-600 text-sm mt-1">{errors.combatRole.message}</p>}
                  {combatRole === 'no' && (
                    <p className="text-amber-600 text-sm mt-2">
                      <i className="fas fa-circle text-yellow-600 mr-1"></i>
                      Note: Non-combat roles will require manual review
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Start Year *
                  </label>
                  <select
                    {...register('serviceStartYear', { required: 'Start year is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select year</option>
                    {Array.from({ length: 30 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>{year}</option>
                      );
                    })}
                  </select>
                  {errors.serviceStartYear && <p className="text-red-600 text-sm mt-1">{errors.serviceStartYear.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service End Year *
                  </label>
                  <select
                    {...register('serviceEndYear', { required: 'End year is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select year</option>
                    {Array.from({ length: 30 }, (_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>{year}</option>
                      );
                    })}
                  </select>
                  {errors.serviceEndYear && <p className="text-red-600 text-sm mt-1">{errors.serviceEndYear.message}</p>}
                </div>
              </div>
            </section>

            {/* Company Description */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Company Description</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  The "Grandma" Test: In one sentence, what does your company do? *
                  <span className="text-sm text-gray-500 block">Explain it simply, without jargon</span>
                </label>
                <textarea
                  {...register('grandmaTest', { 
                    required: 'Company description is required',
                    minLength: { value: 10, message: 'Please provide at least 10 characters' },
                    maxLength: { value: 300, message: 'Please keep it under 300 characters' }
                  })}
                  onChange={(e) => {
                    register('grandmaTest').onChange(e);
                    if (e.target.value.trim().length >= 10) clearErrors('grandmaTest');
                  }}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="We help..."
                />
                {errors.grandmaTest && <p className="text-red-600 text-sm mt-1">{errors.grandmaTest.message}</p>}
                <p className="text-sm text-gray-500 mt-1">
                  {watch('grandmaTest')?.length || 0}/300 characters
                </p>
              </div>
            </section>

            {/* Pitch Deck Upload */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Pitch Deck</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Please upload your current pitch deck
                </label>
                
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                    isDragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input {...getInputProps()} />
                  {deckUploading ? (
                    <p className="text-blue-600">Uploading...</p>
                  ) : deckFile ? (
                    <div>
                      <p className="text-green-600">✓ {deckFile.name} uploaded successfully</p>
                      <p className="text-sm text-gray-500">Click to replace</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-gray-600">Drag & drop your pitch deck here, or click to select</p>
                      <p className="text-sm text-gray-500 mt-1">PDF or PowerPoint files (PDF, PPT, PPTX), max 10MB</p>
                    </div>
                  )}
                </div>
                
                {errors.deckFile && <p className="text-red-600 text-sm mt-1">{errors.deckFile.message}</p>}
                
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    If no deck is available, please explain why
                  </label>
                  <textarea
                    {...register('noDeckReason')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="We're still preparing our presentation..."
                  />
                  {!deckFile && !watch('noDeckReason') && (
                    <p className="text-amber-600 text-sm mt-1">⚠️ Note: No deck or explanation will require manual review</p>
                  )}
                </div>
              </div>
            </section>

            {/* Discovery & Commitment */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Program Information</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    How did you hear about Version Bravo? *
                  </label>
                  <select
                    {...register('howHeardAbout', { required: 'Please tell us how you heard about us' })}
                    onChange={(e) => {
                      register('howHeardAbout').onChange(e);
                      if (e.target.value) clearErrors('howHeardAbout');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select how you heard about us</option>
                    <option value="referral">Referral from a friend/colleague</option>
                    <option value="social-media">Social Media</option>
                    <option value="linkedin">LinkedIn</option>
                    <option value="google-search">Google Search</option>
                    <option value="event">Event or Conference</option>
                    <option value="partner-organization">Partner Organization</option>
                    <option value="veteran-network">Veteran Network</option>
                    <option value="other">Other</option>
                  </select>
                  {errors.howHeardAbout && <p className="text-red-600 text-sm mt-1">{errors.howHeardAbout.message}</p>}
                  {howHeardAbout === 'other' && (
                    <>
                      <input
                        type="text"
                        placeholder="Please specify how you heard about us"
                        {...register('otherHowHeardAbout', { 
                          required: howHeardAbout === 'other' ? 'Please specify how you heard about us' : false 
                        })}
                        className="mt-2 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        onChange={(e) => {
                          register('otherHowHeardAbout').onChange(e);
                          if (e.target.value) clearErrors('otherHowHeardAbout');
                        }}
                      />
                      {errors.otherHowHeardAbout && <p className="text-red-600 text-sm mt-1">{errors.otherHowHeardAbout.message}</p>}
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Time Commitment *
                  </label>
                  <div className="bg-gray-50 p-4 rounded-md mb-4">
                    <p className="text-sm text-gray-700">
                      The program requires mandatory attendance for all virtual sessions and travel for in-person modules. 
                      This includes weekly calls, workshops, and a multi-day intensive program.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        {...register('canCommit', { required: 'Please select your availability' })}
                        value="yes"
                        onChange={(e) => {
                          register('canCommit').onChange(e);
                          if (e.target.value) clearErrors('canCommit');
                        }}
                        className="mr-3"
                      />
                      Yes, I am available to attend and commit to participating 100%
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        {...register('canCommit')}
                        value="no"
                        onChange={(e) => {
                          register('canCommit').onChange(e);
                          if (e.target.value) clearErrors('canCommit');
                        }}
                        className="mr-3"
                      />
                      No, I cannot commit to the full schedule
                    </label>
                  </div>
                  {errors.canCommit && <p className="text-red-600 text-sm mt-1">{errors.canCommit.message}</p>}
                  {canCommit === 'no' && (
                    <p className="text-amber-600 text-sm mt-1">⚠️ Note: Inability to commit will require manual review</p>
                  )}
                </div>
              </div>
            </section>

            {/* Session Selection */}
            {activeCohort && activeCohort.phase2Sessions && activeCohort.phase2Sessions.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Preferred Session Times</h2>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm">
                    <i className="fas fa-info-circle mr-2"></i>
                    Please select at least 1 and at most 2 preferred Group Session times. If approved for Phase 2, we'll confirm which session you'll attend and send calendar details.
                  </p>
                </div>
                
                <div className="space-y-3">
                  {activeCohort.phase2Sessions.map((session) => (
                    <label key={session.id} className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedSessions.includes(session.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            if (selectedSessions.length < 2) {
                              setSelectedSessions([...selectedSessions, session.id]);
                            }
                          } else {
                            setSelectedSessions(selectedSessions.filter(id => id !== session.id));
                          }
                        }}
                        disabled={!selectedSessions.includes(session.id) && selectedSessions.length >= 2}
                        className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {new Date(session.date).toLocaleDateString('en-US', {
                            weekday: 'long',
                            year: 'numeric', 
                            month: 'long',
                            day: 'numeric'
                          })} at {session.time} PST
                        </div>
                        <div className="text-sm text-gray-600">
                          Current signups: {session.currentSignups || 0}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                
                <p className="text-sm text-gray-600 mt-3">
                  Selected: {selectedSessions.length}/2 sessions
                  {selectedSessions.length === 0 && (
                    <span className="text-amber-600 ml-2">
                      <i className="fas fa-exclamation-circle mr-1"></i>
                      Please select at least 1 session
                    </span>
                  )}
                </p>
              </section>
            )}

            {loadingCohort && (
              <section>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Loading Session Times...</h2>
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              </section>
            )}

            {/* Submit Button */}
            <div className="border-t border-gray-200 pt-6">
              <button
                type="submit"
                disabled={loading || countryServed === 'other'}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting Application...' : 'Submit Application'}
              </button>
              
              {countryServed === 'other' && (
                <p className="text-red-600 text-sm mt-2 text-center">
                  Cannot submit: Only US and Israel service members are eligible
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplicationForm;